import { createPortal } from "react-dom";
import { useEffect, useRef } from "react";
import PropTypes from "prop-types";

const overlayStyle = {
  background: "rgba(17, 24, 39, 0.55)",
  zIndex: 1080,
};

export default function BalanceAdjustmentModal({
  isOpen,
  title,
  subtitle,
  submitLabel,
  agents,
  selectedAgent,
  selectedKey,
  onSelectAgent,
  amount,
  reason,
  onChange,
  onClose,
  onSubmit,
  busy = false,
  error = "",
}) {
  const panelRef = useRef(null);
  const latestOnClose = useRef(onClose);

  useEffect(() => {
    latestOnClose.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKeyDown = (event) => {
      if (event.key === "Escape") latestOnClose.current?.();
    };
    document.addEventListener("keydown", handleKeyDown);
    setTimeout(() => panelRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const renderSelectedSummary = () => {
    if (!selectedAgent) {
      return (
        <div className="alert alert-info small mb-3">
          Select an agent balance to continue.
        </div>
      );
    }
    return (
      <div className="alert alert-info small mb-3">
        <div className="fw-semibold">{selectedAgent.name}</div>
        <div>Country: {selectedAgent.countryName || selectedAgent.countryCode || "—"}</div>
        <div>Currency: {selectedAgent.currency || "—"}</div>
        <div>
          Available:{" "}
          <strong>
            {new Intl.NumberFormat("sv-SE", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(Number(selectedAgent.available ?? 0))}{" "}
            {selectedAgent.currency}
          </strong>
        </div>
      </div>
    );
  };

  return createPortal(
    <div
      className="position-fixed top-0 start-0 w-100 h-100"
      style={overlayStyle}
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!busy) onClose?.();
      }}
    >
      <div
        className="d-flex w-100 h-100 justify-content-center align-items-center p-3 p-sm-4"
        onClick={(e) => e.stopPropagation()}
      >
        <section
          className="ui-panel w-100"
          style={{ maxWidth: 520 }}
          tabIndex="-1"
          ref={panelRef}
        >
          <header className="ui-panel__header">
            <div className="d-flex align-items-start justify-content-between gap-3">
              <div>
                <h3 className="ui-title mb-0">{title}</h3>
                {subtitle && <p className="ui-subtitle mb-0 mt-1">{subtitle}</p>}
              </div>
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm rounded-pill"
                onClick={onClose}
                disabled={busy}
              >
                Close
              </button>
            </div>
          </header>

          <form onSubmit={onSubmit} className="p-3 p-sm-4">
            <div className="mb-3">
              <label className="form-label fw-semibold" htmlFor="adjust-agent">
                Agent & balance
              </label>
              <select
                id="adjust-agent"
                className="form-select"
                value={selectedKey}
                onChange={(e) => onSelectAgent?.(e.target.value)}
                disabled={busy || agents.length === 0}
                required
              >
                <option value="">-- Select agent balance --</option>
                {agents.map((agent) => (
                  <option key={agent.key} value={agent.key}>
                    {agent.name} · {agent.countryName || agent.countryCode} ({agent.currency})
                  </option>
                ))}
              </select>
              <div className="form-text">
                Only agents with balance snapshots appear in this list.
              </div>
            </div>

            {renderSelectedSummary()}

            <div className="mb-3">
              <label className="form-label fw-semibold" htmlFor="adjust-amount">
                Amount
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  {selectedAgent?.currency || "CUR"}
                </span>
                <input
                  id="adjust-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  inputMode="decimal"
                  className="form-control"
                  value={amount}
                  onChange={(e) => onChange?.("amount", e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
            </div>

            <div className="mb-3">
              <label className="form-label fw-semibold" htmlFor="adjust-reason">
                Reason
              </label>
              <textarea
                id="adjust-reason"
                className="form-control"
                rows={3}
                value={reason}
                onChange={(e) => onChange?.("reason", e.target.value)}
                placeholder="Describe why this adjustment is needed"
                required
              />
            </div>

            {error && <div className="alert alert-danger py-2">{error}</div>}

            <div className="d-flex justify-content-end gap-2 pt-2">
              <button
                type="button"
                className="btn btn-outline-secondary rounded-pill"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary rounded-pill"
                disabled={busy || agents.length === 0}
              >
                {busy ? "Processing…" : submitLabel}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>,
    document.body
  );
}

BalanceAdjustmentModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  submitLabel: PropTypes.string.isRequired,
  agents: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      countryCode: PropTypes.string,
      countryName: PropTypes.string,
      currency: PropTypes.string,
      available: PropTypes.number,
    })
  ).isRequired,
  selectedAgent: PropTypes.object,
  selectedKey: PropTypes.string.isRequired,
  onSelectAgent: PropTypes.func.isRequired,
  amount: PropTypes.string.isRequired,
  reason: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  busy: PropTypes.bool,
  error: PropTypes.string,
};
